/**
 * Recursos do Kanban (CRM) que o ponteiro de uma novidade sabe apontar. Ver `spotlightTargets.js`.
 *
 * Quase tudo daqui é do quadro do CRM INTERNO: uma conta com AdvBox, Projuris ou outro sistema como
 * quadro padrão vê a agenda e a gestão de atividades, mas não a barra, as colunas e a gaveta do
 * negócio; o ponteiro desses espera em silêncio. Os recursos que mexem em dados pedem `crm.manage`
 * (quem só lê vê um quadro somente leitura).
 */

const FEATURE = "crm.enabled";
const MANAGE = "crm.manage";

const BARRA = "Barra do quadro";
const QUADRO = "Quadro e cartões";
const GAVETA = "Gaveta do negócio";
const AGENDA = "Agenda de atividades";
const GESTAO = "Gestão de atividades";

const ABRIR_NEGOCIO = { key: "crm.deal-card", text: "Abra um negócio clicando no cartão" };
const ABRIR_GESTAO = { key: "crm.activity-drawer-button", text: "Abra a Gestão de atividades" };
const stepsDoNegocio = (what) => [{ ...ABRIR_NEGOCIO, text: `${ABRIR_NEGOCIO.text} para ver ${what}.` }];
const stepsDaGestao = (what) => [{ ...ABRIR_GESTAO, text: `${ABRIR_GESTAO.text} para ver ${what}.` }];
const stepsDaAbaDaGestao = (tab) => [
  { ...ABRIR_GESTAO, text: `${ABRIR_GESTAO.text}.` },
  { key: "crm.activity-tabs", text: `Escolha a aba ${tab}.` },
];
const stepsDaAbaDoNegocio = (tab) => [
  { ...ABRIR_NEGOCIO, text: `${ABRIR_NEGOCIO.text}.` },
  { key: "crm.deal-tabs", text: `Escolha a aba ${tab}.` },
];

export const CRM_TARGETS = [
  // ── Barra do quadro ─────────────────────────────────────────────────────
  {
    key: "crm.view-toggle",
    navKey: "crm",
    group: BARRA,
    label: "Alternar quadro e lista",
    path: "/crm",
    requires: "page",
    feature: FEATURE,
    hint: "Só no CRM interno: troca o quadro de colunas pela tabela de negócios, com seleção em lote.",
  },
  {
    key: "crm.new-deal",
    navKey: "crm",
    group: BARRA,
    label: "Botão + Negócio",
    path: "/crm",
    requires: "page",
    feature: FEATURE,
    permission: MANAGE,
    hint: "Só no CRM interno e para quem gerencia o CRM: cria um negócio com contato, funil, etapa, valor e responsável.",
  },
  {
    key: "crm.search",
    navKey: "crm",
    group: BARRA,
    label: "Busca de negócios",
    path: "/crm",
    requires: "page",
    feature: FEATURE,
    hint: "Só no CRM interno: procura pelo nome do negócio ou do contato, no quadro e na lista.",
  },
  {
    key: "crm.pipeline-selector",
    navKey: "crm",
    group: BARRA,
    label: "Seletor de funil (pipeline)",
    path: "/crm",
    requires: "page",
    feature: FEATURE,
    hint: "Só no CRM interno: troca de funil; quem gerencia também edita ou cria funis aqui.",
  },
  {
    key: "crm.pipeline-columns",
    navKey: "crm",
    group: BARRA,
    label: "Botão Colunas",
    path: "/crm",
    requires: "page",
    feature: FEATURE,
    permission: MANAGE,
    hint: "Só no CRM interno e para quem gerencia: renomeia as colunas do quadro e acrescenta etapas.",
  },
  {
    key: "crm.automations",
    navKey: "crm",
    group: BARRA,
    label: "Automatizar (Beta)",
    path: "/crm",
    requires: "page",
    feature: FEATURE,
    permission: MANAGE,
    hint: "Só no CRM interno e para quem gerencia: regras do tipo \"quando acontecer X, faça Y\" para negócios e tarefas.",
  },
  {
    key: "crm.owner-filter",
    navKey: "crm",
    group: BARRA,
    label: "Filtro por responsável",
    path: "/crm",
    requires: "page",
    feature: FEATURE,
    hint: "Só no CRM interno: mostra só os negócios de uma pessoa; dá para favoritar quem se filtra sempre.",
  },
  {
    key: "crm.more-menu",
    navKey: "crm",
    group: BARRA,
    label: "Menu \"...\" (exportar, personalizar cartões)",
    path: "/crm",
    requires: "page",
    feature: FEATURE,
    hint: "Só no CRM interno: exporta os negócios filtrados em CSV ou Excel, atualiza o quadro e personaliza os cartões.",
  },
  {
    key: "crm.status-filter",
    navKey: "crm",
    group: BARRA,
    label: "Abertos, Ganhos e Perdidos",
    path: "/crm",
    requires: "page",
    feature: FEATURE,
    hint: "Só no CRM interno: alterna entre negócios abertos, ganhos e perdidos.",
  },
  {
    key: "crm.conditions",
    navKey: "crm",
    group: BARRA,
    label: "Condições (filtros avançados)",
    path: "/crm",
    requires: "page",
    feature: FEATURE,
    hint: "Só no CRM interno: filtra por etapa, faixa de valor, data de criação e previsão de fechamento.",
  },
  {
    key: "crm.sort",
    navKey: "crm",
    group: BARRA,
    label: "Ordenar por",
    path: "/crm",
    requires: "page",
    feature: FEATURE,
    hint: "Só no CRM interno: ordena por valor, data de criação, última atualização ou previsão de fechamento.",
  },

  // ── Quadro e cartões ────────────────────────────────────────────────────
  {
    key: "crm.board",
    navKey: "crm",
    group: QUADRO,
    label: "Quadro Kanban (colunas por etapa)",
    path: "/crm",
    requires: "page",
    feature: FEATURE,
    hint: "Só no CRM interno: cada coluna é uma etapa do funil; os cartões se arrastam para avançar o negócio.",
  },
  {
    key: "crm.column",
    navKey: "crm",
    group: QUADRO,
    label: "Coluna (etapa) com total e valor",
    path: "/crm",
    requires: "page",
    feature: FEATURE,
    hint: "Só no CRM interno: o cabeçalho mostra quantos negócios e quanto dinheiro há na etapa. Aponta a primeira coluna.",
  },
  {
    key: "crm.column-add-deal",
    navKey: "crm",
    group: QUADRO,
    label: "+ Negócio dentro da coluna",
    path: "/crm",
    requires: "page",
    feature: FEATURE,
    permission: MANAGE,
    hint: "Só no CRM interno e para quem gerencia: cria o negócio já na etapa que a pessoa está olhando.",
  },
  {
    key: "crm.deal-card",
    navKey: "crm",
    group: QUADRO,
    label: "Cartão de negócio",
    path: "/crm",
    requires: "data",
    feature: FEATURE,
    hint: "Só no CRM interno e com ao menos um negócio: clicar abre o negócio; arrastar muda de etapa. Aponta o primeiro cartão.",
  },
  {
    key: "crm.list-view",
    navKey: "crm",
    group: QUADRO,
    label: "Lista de negócios (tabela)",
    path: "/crm",
    requires: "panel",
    feature: FEATURE,
    hint: "Só no CRM interno: todos os negócios em tabela paginada, com seleção para ações em lote.",
    steps: [{ key: "crm.view-toggle", text: "Clique no ícone de lista para ver a tabela de negócios." }],
  },

  // ── Gaveta do negócio ───────────────────────────────────────────────────
  {
    key: "crm.deal-drawer",
    navKey: "crm",
    group: GAVETA,
    label: "Gaveta do negócio",
    path: "/crm",
    requires: "open-deal",
    feature: FEATURE,
    hint: "Tudo sobre o negócio num lugar só: dados, checklist, anexos, agenda e conversa interna.",
    steps: stepsDoNegocio("a gaveta do negócio"),
  },
  {
    key: "crm.deal-title-value",
    navKey: "crm",
    group: GAVETA,
    label: "Editar título e valor",
    path: "/crm",
    requires: "open-deal",
    feature: FEATURE,
    permission: MANAGE,
    hint: "Clicar no título ou no valor do negócio para corrigi-los.",
    steps: stepsDoNegocio("o título e o valor"),
  },
  {
    key: "crm.deal-stage-progress",
    navKey: "crm",
    group: GAVETA,
    label: "Barra de etapas do negócio",
    path: "/crm",
    requires: "open-deal",
    feature: FEATURE,
    hint: "Clicar numa etapa da barra move o negócio sem sair da gaveta. Só em negócios abertos.",
    steps: stepsDoNegocio("a barra de etapas"),
  },
  {
    key: "crm.deal-outcome-actions",
    navKey: "crm",
    group: GAVETA,
    label: "Ganho, Perdido, Reabrir e Excluir",
    path: "/crm",
    requires: "open-deal",
    feature: FEATURE,
    permission: MANAGE,
    hint: "Fecha o negócio como ganho ou perdido (com motivo), reabre ou exclui.",
    steps: stepsDoNegocio("os botões de ganho e perdido"),
  },
  {
    key: "crm.deal-tabs",
    navKey: "crm",
    group: GAVETA,
    label: "Abas Detalhes, Checklist e Anexos",
    path: "/crm",
    requires: "open-deal",
    feature: FEATURE,
    hint: "Detalhes traz campos e agenda, Checklist o processo da etapa, Anexos os documentos.",
    steps: stepsDoNegocio("as abas"),
  },
  {
    key: "crm.deal-agenda",
    navKey: "crm",
    group: GAVETA,
    label: "Agenda do negócio (atividades)",
    path: "/crm",
    requires: "open-deal",
    feature: FEATURE,
    hint: "Agenda ligação, reunião ou tarefa no negócio, com lembrete e link do Google Meet.",
    steps: stepsDoNegocio("a agenda do negócio"),
  },
  {
    key: "crm.deal-checklist",
    navKey: "crm",
    group: GAVETA,
    label: "Checklist do negócio",
    path: "/crm",
    requires: "open-deal",
    feature: FEATURE,
    hint: "O passo a passo da etapa; modelos automáticos criam a lista quando o negócio entra na etapa.",
    steps: stepsDaAbaDoNegocio("Checklist"),
  },
  {
    key: "crm.deal-attachments",
    navKey: "crm",
    group: GAVETA,
    label: "Anexos do negócio",
    path: "/crm",
    requires: "open-deal",
    feature: FEATURE,
    hint: "Guarda contrato, proposta e documentos no próprio negócio (até 25 MB por arquivo).",
    steps: stepsDaAbaDoNegocio("Anexos"),
  },
  {
    key: "crm.deal-notes-timeline",
    navKey: "crm",
    group: GAVETA,
    label: "Notas e atividade (comentários e histórico)",
    path: "/crm",
    requires: "open-deal",
    feature: FEATURE,
    hint: "O ícone de painel no topo da gaveta abre o feed de comentários da equipe e o histórico do negócio.",
    steps: stepsDoNegocio("o ícone de notas e atividade"),
  },

  // ── Agenda de atividades (faixa no topo) ────────────────────────────────
  {
    key: "crm.agenda",
    navKey: "crm",
    group: AGENDA,
    label: "Agenda de atividades (faixa no topo do Kanban)",
    path: "/crm",
    requires: "page",
    hint: "Aparece quando a pessoa abre o Kanban. Com a faixa recolhida, o cabeçalho continua visível.",
  },
  {
    key: "crm.agenda-period",
    navKey: "crm",
    group: AGENDA,
    label: "Período da agenda",
    path: "/crm",
    requires: "page",
    hint: "Só com a faixa expandida: troca entre hoje, semana, mês ou um intervalo; supervisores veem a agenda de outra pessoa.",
  },
  {
    key: "crm.agenda-collapse",
    navKey: "crm",
    group: AGENDA,
    label: "Recolher e expandir a faixa",
    path: "/crm",
    requires: "page",
    hint: "Esconde a faixa para ganhar espaço no quadro; a preferência fica salva.",
  },
  {
    key: "crm.activity-drawer-button",
    navKey: "crm",
    group: AGENDA,
    label: "Botão Gestão de atividades",
    path: "/crm",
    requires: "page",
    hint: "Abre o painel completo de tarefas: meu dia, visão geral, lista, calendário e lembretes.",
  },
  {
    key: "crm.overdue-pill",
    navKey: "crm",
    group: AGENDA,
    label: "Selo de atividades atrasadas",
    path: "/crm",
    requires: "data",
    hint: "Só quando há atividade atrasada: um alerta vermelho conta quantas e abre a lista.",
  },

  // ── Gestão de atividades (painel) ───────────────────────────────────────
  {
    key: "crm.activity-drawer",
    navKey: "crm",
    group: GESTAO,
    label: "Painel Gestão de atividades",
    path: "/crm",
    requires: "panel",
    hint: "Um único lugar para trabalhar as tarefas da equipe e as dos sistemas jurídicos.",
    steps: [{ ...ABRIR_GESTAO, text: `${ABRIR_GESTAO.text}.` }],
  },
  {
    key: "crm.activity-tabs",
    navKey: "crm",
    group: GESTAO,
    label: "Abas da gestão (Meu dia, Visão geral, Lista, Calendário, Lembretes)",
    path: "/crm",
    requires: "panel",
    hint: "Cinco abas: o dia, os números, a lista completa, o mês e os avisos.",
    steps: stepsDaGestao("as abas"),
  },
  {
    key: "crm.activity-new",
    navKey: "crm",
    group: GESTAO,
    label: "Nova atividade",
    path: "/crm",
    requires: "panel",
    hint: "Cria uma atividade ligada a um negócio, ou uma tarefa no sistema jurídico, com data e responsável.",
    steps: stepsDaGestao("o botão Nova atividade"),
  },
  {
    key: "crm.activity-myday",
    navKey: "crm",
    group: GESTAO,
    label: "Aba Meu dia",
    path: "/crm",
    requires: "panel",
    hint: "Seu dia em lista de trabalho, com os números do topo funcionando como filtros.",
    steps: stepsDaAbaDaGestao("Meu dia"),
  },
  {
    key: "crm.activity-quickadd",
    navKey: "crm",
    group: GESTAO,
    label: "Atividade rápida (\"Ligar para a Ana às 17h\")",
    path: "/crm",
    requires: "panel",
    hint: "Só na aba Meu dia e para atividades do CRM: digite uma frase com dia e hora e a atividade é criada.",
    steps: stepsDaAbaDaGestao("Meu dia"),
  },
  {
    key: "crm.activity-overview",
    navKey: "crm",
    group: GESTAO,
    label: "Aba Visão geral",
    path: "/crm",
    requires: "panel",
    hint: "Números e gráficos: carga dos próximos dias, pendentes por etapa e lembretes do dia.",
    steps: stepsDaAbaDaGestao("Visão geral"),
  },
  {
    key: "crm.activity-list",
    navKey: "crm",
    group: GESTAO,
    label: "Aba Lista",
    path: "/crm",
    requires: "panel",
    hint: "Todas as atividades, com busca, filtro por tipo, situação e ordenação.",
    steps: stepsDaAbaDaGestao("Lista"),
  },
  {
    key: "crm.activity-calendar",
    navKey: "crm",
    group: GESTAO,
    label: "Aba Calendário",
    path: "/crm",
    requires: "panel",
    hint: "O mês inteiro; escolha um dia e veja as atividades dele ao lado.",
    steps: stepsDaAbaDaGestao("Calendário"),
  },
  {
    key: "crm.activity-reminders",
    navKey: "crm",
    group: GESTAO,
    label: "Aba Lembretes",
    path: "/crm",
    requires: "panel",
    hint: "Escolha como ser avisado (sino, navegador, e-mail, resumo diário) e com quanta antecedência.",
    steps: stepsDaAbaDaGestao("Lembretes"),
  },
];
